import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { MealSlotEditor, type SlotSavePayload } from "../components/MealSlotEditor";
import { dayNameCs, formatDateCs, MEAL_TYPE_LABELS, rangeDates } from "../lib/utils";
import type { Ingredient, MealPlan, MealPlanEntry, Recipe, Store } from "../types";

interface MealPlanDetail {
  mealPlan: MealPlan;
  entries: MealPlanEntry[];
}

export default function MealPlanDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();

  const planQ = useQuery({
    queryKey: ["meal-plan", id],
    queryFn: () => api.get<MealPlanDetail>(`/api/meal-plans/${id}`),
  });
  const recipesQ = useQuery({
    queryKey: ["recipes"],
    queryFn: () => api.get<{ recipes: Recipe[] }>("/api/recipes"),
  });
  const storesQ = useQuery({
    queryKey: ["stores"],
    queryFn: () => api.get<{ stores: Store[] }>("/api/stores"),
  });
  const ingredientsQ = useQuery({
    queryKey: ["ingredients"],
    queryFn: () => api.get<{ ingredients: Ingredient[] }>("/api/ingredients"),
  });

  const [editSlot, setEditSlot] = useState<{ date: string; mealType: string; entry?: MealPlanEntry } | null>(null);

  const upsert = useMutation({
    mutationFn: (payload: SlotSavePayload) => api.put(`/api/meal-plans/${id}/entries`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meal-plan", id] });
      qc.invalidateQueries({ queryKey: ["recipes"] });
    },
  });
  const removeEntry = useMutation({
    mutationFn: (entryId: string) => api.delete(`/api/meal-plans/${id}/entries?entryId=${entryId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["meal-plan", id] }),
  });
  const toggleCooked = useMutation({
    mutationFn: ({ entryId, cooked }: { entryId: string; cooked: boolean }) =>
      api.post(`/api/meal-plans/${id}/entries`, { entryId, cooked }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meal-plan", id] });
      qc.invalidateQueries({ queryKey: ["fridge"] });
    },
  });

  const entriesByKey = useMemo(() => {
    const map = new Map<string, MealPlanEntry>();
    for (const e of planQ.data?.entries ?? []) {
      map.set(`${e.date}|${e.mealType}`, e);
    }
    return map;
  }, [planQ.data]);

  if (!planQ.data) return <p className="text-slate-500">Načítám…</p>;

  const plan = planQ.data.mealPlan;
  const days = rangeDates(plan.dateFrom, plan.dateTo);
  const mealTypes = plan.mealTypes ?? [];
  const recipesList = recipesQ.data?.recipes ?? [];
  const storesList = storesQ.data?.stores ?? [];
  const ingredientsList = ingredientsQ.data?.ingredients ?? [];

  return (
    <div className="print:text-black">
      <Link to="/jidelnicky" className="text-sm text-slate-500 hover:underline print:hidden">
        ← Zpět na jídelníčky
      </Link>

      <div className="print:hidden">
        <PageHeader
          icon="📅"
          title={plan.name}
          description={`${formatDateCs(plan.dateFrom)} – ${formatDateCs(plan.dateTo)}`}
          actions={
            <>
              <DefaultServingsEditor planId={id} value={plan.defaultServings ?? 2} />
              <button
                className="btn-secondary flex items-center gap-1"
                onClick={() => window.print()}
                title="Otevře dialog tisku. V něm vyberte tiskárnu, nebo „Uložit jako PDF“ pro export."
              >
                <span aria-hidden>🖨️</span>
                <span>Tisk / PDF</span>
              </button>
              {storesList.length > 0 ? (
                <details className="relative">
                  <summary className="btn-primary cursor-pointer list-none flex items-center gap-1">
                    <span aria-hidden>🛒</span>
                    <span>Vygenerovat nákupní seznam ▾</span>
                  </summary>
                  <div className="absolute right-0 mt-2 bg-white border rounded-md shadow-md z-10 min-w-[200px]">
                    {storesList.map((s) => (
                      <Link
                        key={s.id}
                        to={`/jidelnicky/${id}/nakup/${s.id}`}
                        className="block px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                      >
                        <span aria-hidden>🏪</span>
                        <span>{s.name}</span>
                      </Link>
                    ))}
                  </div>
                </details>
              ) : (
                <Link to="/obchody" className="btn-secondary flex items-center gap-1">
                  <span aria-hidden>➕</span>
                  <span>Vytvořit obchod</span>
                </Link>
              )}
            </>
          }
        />
      </div>

      {/* Hlavička pro tiskový výstup – jednoduchá, bez ikon a tlačítek. */}
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-bold text-black">{plan.name}</h1>
        <p className="text-sm text-slate-700">
          {formatDateCs(plan.dateFrom)} – {formatDateCs(plan.dateTo)}
          {mealTypes.length > 0 && (
            <> · {mealTypes.map((mt) => MEAL_TYPE_LABELS[mt] ?? mt).join(", ")}</>
          )}
        </p>
      </div>

      <div className="card overflow-x-auto print:overflow-visible print:border-0 print:shadow-none print:rounded-none">
        <table className="w-full text-sm print:text-xs">
          <thead className="bg-slate-50 text-left print:bg-white">
            <tr>
              <th className="px-3 py-2 w-32 print:border print:border-slate-400">Den</th>
              {mealTypes.map((mt) => (
                <th key={mt} className="px-3 py-2 print:border print:border-slate-400">
                  {MEAL_TYPE_LABELS[mt] ?? mt}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y print:divide-y-0">
            {days.map((d) => (
              <tr key={d} className="print:break-inside-avoid">
                <td className="px-3 py-2 align-top print:border print:border-slate-400">
                  <div className="font-medium capitalize">{dayNameCs(d)}</div>
                  <div className="text-xs text-slate-500 print:text-slate-700">{formatDateCs(d)}</div>
                </td>
                {mealTypes.map((mt) => {
                  const e = entriesByKey.get(`${d}|${mt}`);
                  const recipe = e?.recipe ?? recipesList.find((r) => r.id === e?.recipeId);
                  const hasMeal = !!e && (!!e.recipeId || !!e.customTitle);
                  const displayName =
                    recipe?.name ?? e?.customTitle ?? (hasMeal ? "(vlastní jídlo)" : "");
                  return (
                    <td key={mt} className="px-3 py-2 align-top print:border print:border-slate-400">
                      {hasMeal ? (
                        <div
                          className={
                            "rounded border bg-white p-2 cursor-pointer hover:border-brand-400 print:cursor-default print:border-0 print:p-0 " +
                            (e.cooked ? "bg-emerald-50 border-emerald-300 print:bg-white" : "")
                          }
                          onClick={() => setEditSlot({ date: d, mealType: mt, entry: e })}
                        >
                          <div className="font-medium text-sm flex items-center gap-1 print:text-black">
                            {e.cooked && <span aria-hidden>✅</span>}
                            <span>{displayName}</span>
                            {!e.recipeId && e.customTitle && (
                              <span className="text-xs font-normal text-slate-400">· vlastní</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-1 print:text-slate-700">
                            {e.recipeId ? (
                              <>
                                <span className="print:hidden">🍽️ </span>
                                {e.servings} porce
                              </>
                            ) : (
                              <>
                                <span className="print:hidden">🥕 </span>
                                {(e.customIngredients?.length ?? 0) > 0
                                  ? `${e.customIngredients!.length} surovin`
                                  : "vlastní jídlo"}
                              </>
                            )}
                          </div>
                          <div className="text-xs mt-1 flex items-center gap-2 print:hidden">
                            <label
                              className="inline-flex items-center gap-1"
                              onClick={(ev) => ev.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={!!e.cooked}
                                onChange={() => toggleCooked.mutate({ entryId: e.id, cooked: !e.cooked })}
                              />
                              <span>🍳 uvařeno</span>
                            </label>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            className="w-full rounded border border-dashed border-slate-300 text-xs text-slate-400 p-2 hover:border-brand-400 hover:text-brand-600 flex items-center justify-center gap-1 print:hidden"
                            onClick={() => setEditSlot({ date: d, mealType: mt, entry: e })}
                          >
                            <span aria-hidden>➕</span>
                            <span>přidat</span>
                          </button>
                          <span className="hidden print:inline text-slate-400">—</span>
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editSlot && (
        <MealSlotEditor
          slot={editSlot}
          recipes={recipesList}
          ingredients={ingredientsList}
          defaultServings={plan.defaultServings ?? 2}
          onClose={() => setEditSlot(null)}
          onSave={(payload) => {
            upsert.mutate(payload, {
              onSuccess: () => setEditSlot(null),
            });
          }}
          onRemove={() => {
            if (editSlot.entry) removeEntry.mutate(editSlot.entry.id);
            setEditSlot(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Inline editor výchozího počtu porcí pro celý jídelníček.
 * Klik → input → blur/Enter → PATCH /api/meal-plans/:id. Při změně se invaliduje
 * cache jídelníčku, aby se nově otevřený SlotEditor předvyplnil novou hodnotou.
 */
function DefaultServingsEditor({ planId, value }: { planId: string; value: number }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<number | "">(value);

  const save = useMutation({
    mutationFn: (v: number) => api.patch(`/api/meal-plans/${planId}`, { defaultServings: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["meal-plan", planId] }),
  });

  const commit = () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || n <= 0) {
      setDraft(value);
      setEditing(false);
      return;
    }
    if (n !== value) save.mutate(n);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 bg-white text-sm">
        <span aria-hidden>🍽️</span>
        <input
          type="number"
          className="w-16 text-sm bg-transparent focus:outline-none"
          min={0.5}
          step={0.5}
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value === "" ? "" : Number(e.target.value))}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setDraft(value);
              setEditing(false);
            }
          }}
        />
        <span className="text-slate-500 text-xs">porcí</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="btn-ghost text-sm flex items-center gap-1"
      title="Výchozí počet porcí pro nově přidávaná jídla. Klikněte pro úpravu."
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
    >
      <span aria-hidden>🍽️</span>
      <span>
        Výchozí: <strong>{value}</strong> porcí
      </span>
      <span aria-hidden className="text-xs text-slate-400">✏️</span>
    </button>
  );
}
